import React from "react"
import { BodyText, InlineText, SubTitle } from "../../Typography/Typography"
import SmartLink from "../../layout/SmartLink/SmartLink"

const providerProfileClickEvent = () => ({
  name: "SEO Filtered Directory - Copy link to provider clicked",
  properties: {
    "Care Type": "care_home",
    "GEO Location": "London",
    "Current URL":
      "https://www.caresourcer.com/s/providers/towns/london/care-home/",
  },
})

export default {
  summary: (
    <BodyText marginBottom>
      Choosing the right care home for a person you care for is no easy task -
      with many care searchers describing the process as a minefield to
      navigate. That’s where our care home directory can help - with over 400
      care homes to choose from in London. If you weren’t aware already, all
      care homes in London are regulated by the Care Quality Commission (CQC) -
      and of the 421 care homes in London, 331 have been rated good or above.
      CQC ratings are important, of course, but the right choice for your loved
      one comes down to your loved one’s care needs and personal choices. We
      hope our care directory and advice helps you choose the right care home in
      London for your loved one.
    </BodyText>
  ),
  content: [
    <SubTitle marginBottom bold>
      Location is key
    </SubTitle>,
    <BodyText marginBottom>
      Location, location, location is a key factor when choosing where you want
      to live - and picking the right care home for your loved one is no
      different. London is a vast city, and because of this, care homes can be
      situated in a number of different environments, whether towards a leafier
      area like
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-122477519/compton-lodge"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Compton Lodge</InlineText>
      </SmartLink>
      which is a hop skip and a jump away from Primrose Hill, or perhaps the
      option of being near the water might make them happy - like
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-127818748/meadbank-care-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Meadbank Care Home</InlineText>
      </SmartLink>
      in Battersea. Think about how easy the care home is to visit your loved
      ones, because transportation links - and even parking accessibility -
      needs to be thought about when living in London.
    </BodyText>,
    <SubTitle marginBottom bold>
      Feeling at home
    </SubTitle>,
    <BodyText marginBottom>
      What would make your loved one feel most at home and happy? Do they like
      to keep active and be around people? Or do they enjoy time relaxing and
      prefer their own company? Before making a decision on the best care home
      for them, we recommend thinking about: Think about what makes your loved
      one happy. Do they enjoy quiet time watching TV or reading? Or are they
      happier being around people and staying active? Here are a few things to
      consider:
    </BodyText>,
    <SubTitle marginBottom bold italic>
      Gardens and outdoor spaces
    </SubTitle>,
    <BodyText marginBottom>
      Getting out in the fresh air can work wonders for the body and mind -
      which is why a lot of care homes in London boast green spaces within their
      grounds for residents.
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-5101202892/beaumont-court-care-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Beaumont Court Care Home</InlineText>
      </SmartLink>{" "}
      is one such property which offers gardens and grounds for residents and
      their family and friends to enjoy.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Pets welcome
    </BodyText>,
    <BodyText marginBottom>
      It has been proven that having pets can raise serotonin levels and reduce
      stress - which is why a number of care homes in London allow residents to
      bring their pets with them. Of course, this needs to be arranged ahead of
      your furry companion’s arrival, but care homes like
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-128288766/the-highgate-care-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>The Highgate Care Home</InlineText>
      </SmartLink>
      and
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-126242004/norton-house"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Norton House</InlineText>
      </SmartLink>
      both welcome the pets of their residents.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Activities and days out
    </BodyText>,
    <BodyText marginBottom>
      Most care homes work hard to offer residents a mix of spaces to relax in
      and spaces that will be a hive of activity. Homes in London are no
      different, with many offering a number of on-site facilities including
      libraries, cinemas, hair salons, cafes and a range of group-based
      activities.
    </BodyText>,
  ],
}
